import { readNativeInputReport, currentLgFieldSessionId } from './nativeTvInputs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useSocket } from '../../context/SocketContext';
import UnifiedTvControl from './UnifiedTvControl';
import LgCommissioningPanel from './LgCommissioningPanel';
import AdbRemoteReboot from './AdbRemoteReboot';
import RemoteAppPicker from './RemoteAppPicker';
import RemoteFieldResults from './RemoteFieldResults';
import AdbRemotePreview from './AdbRemotePreview';
import PreviewPopout from './PreviewPopout';
import { commandAttemptSummary } from '../Remote/remoteCommandFeedback';
import useRemoteHdmiInputs from './useRemoteHdmiInputs';
import { requestRemoteState } from './remoteStateApi';
import { requestLgSnapshot } from './lgPreviewApi';
import { LG_TV_COMMANDS, UNIFIED_TV_COMMANDS } from './unifiedTvControlCommands';
import useSharedRemoteRuntime, {
  formatCapabilities,
  formatDiscovery,
  formatPairingStatus,
  formatPorts,
  METHOD_LABELS,
  PREVIEW_PRESETS,
} from '../Remote/useSharedRemoteRuntime';

function TvRemotePanel({ device, onDeviceUpdated, onSourceTargetSelected }) {
  const inputScope = useRef({ deviceId: device?.id });
  if (inputScope.current.deviceId !== device?.id) inputScope.current = { deviceId: device?.id };
  const token = localStorage.getItem('token');
  const socket = useSocket();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewWindowVisible, setPreviewWindowVisible] = useState(false);
  const [previewProvider, setPreviewProvider] = useState('native');
  const [siteControlTarget, setSiteControlTarget] = useState(null);
  const [sourceContextError, setSourceContextError] = useState('');
  const sourceContextScope = useRef({ deviceId: device?.id, generation: 0 });
  if (sourceContextScope.current.deviceId !== device?.id) sourceContextScope.current = { deviceId: device?.id, generation: sourceContextScope.current.generation + 1 };
  const [lgInputs, setLgInputs] = useState([]);
  const [rebootBusy, setRebootBusy] = useState(false);
  const [appPickerDeviceId, setAppPickerDeviceId] = useState(null);
  const augmentedDevice = useMemo(() => {
    if (!siteControlTarget || Number(siteControlTarget.deviceId) !== Number(device?.id)) return device;
    return {
      ...device,
      sourceLayout: siteControlTarget.sourceLayout || device?.sourceLayout || null,
      capabilities: {
        ...(device?.capabilities || {}),
        lgTarget: siteControlTarget.protocol === 'lg_webos_remote' ? {
          paired: siteControlTarget.paired, host: siteControlTarget.host,
          relayDeviceId: siteControlTarget.lastBridgeDeviceId || siteControlTarget.controlledBy,
          lastConnectedAt: siteControlTarget.lastConnectedAt, lastError: siteControlTarget.lastError,
        } : null,
        androidTvRemote: siteControlTarget.googleRemote || { paired: false },
        adbRemote: {
          configured: Boolean(siteControlTarget.adb),
          authorized: Boolean(siteControlTarget.adb &&
            ['paired', 'not_required'].includes(siteControlTarget.adbAuthorization?.trustState) &&
            !siteControlTarget.adbAuthorization?.endpointNeedsVerification),
        },
      },
    };
  }, [device, siteControlTarget]);
  const hdmiInputs = useRemoteHdmiInputs({ deviceId: device?.id, sourceLayout: augmentedDevice?.sourceLayout });
  const adbHdmiPorts = useMemo(() => hdmiInputs.sources.map((entry) => entry.port), [hdmiInputs.sources]);

  useEffect(() => {
    let cancelled = false;
    setSiteControlTarget(null);
    setLgInputs([]);
    setSourceContextError('');
    setAppPickerDeviceId(null);
    if (!device?.id || !token) return undefined;
    const loadSiteControlTarget = async () => {
      try {
        const res = await axios.get(`/api/site-control/targets/${device.id}/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        setSiteControlTarget(res.data?.target || null);
        setLgInputs(readNativeInputReport(res.data?.target)?.inputs || []);
      } catch (error) {
        if (!cancelled) setSiteControlTarget(null);
      }
    };
    loadSiteControlTarget();
    return () => {
      cancelled = true;
    };
  }, [device?.id, token]);

  const transport = useMemo(() => ({
    requestState: requestRemoteState,
    requestLgSnapshot,
    loadApps: async ({ deviceId }) => {
      const res = await fetch(`/api/devices/get-installed-apps?deviceId=${deviceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to load apps');
      }
      return Array.isArray(data.apps) ? data.apps : [];
    },
    launchApp: async ({ deviceId, packageName }) => {
      const res = await axios.post(
        `/api/external-apps/devices/${deviceId}/packages/${encodeURIComponent(packageName)}/launch`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data;
    },
    updateIntegration: ({ deviceId, ...payload }) =>
      axios.post(
        '/api/devices/update-integration',
        { deviceId, ...payload },
        { headers: { Authorization: `Bearer ${token}` } }
      ),
    updateSettings: ({ deviceId, settings }) =>
      axios.post(
        '/api/devices/update-settings',
        { deviceId, settings },
        { headers: { Authorization: `Bearer ${token}` } }
      ),
    sendProtocolCommand: async ({ deviceId, command, protocol, requestId }) => {
      const res = await axios.post(
        '/api/command',
        { deviceId, command, protocol, requestId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data;
    },
    sendAssistantCommand: async ({ deviceId, command, params }) => {
      const res = await axios.post(
        '/api/googlehome/assistant-query',
        { deviceId, command, params },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data;
    },
    sendRoutedAction: async ({ deviceId, action, requestId, controlMethod }) => {
      const res = await axios.post(
        `/api/external-apps/devices/${deviceId}/actions`,
        { action, requestId, controlMethod },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data;
    },
    sendTargetAction: async ({ deviceId, ...payload }) => {
      if (payload.controlMethod === 'lg_webos_remote' && siteControlTarget?.siteId) {
        const key = `lgFieldSession:${siteControlTarget.siteId}`;
        let saved;
        try { saved = JSON.parse(localStorage.getItem(key)); } catch { saved = null; }
        if (!saved?.id || Date.now() - saved.startedAt >= 86400000) {
          const bytes = crypto.getRandomValues(new Uint8Array(16));
          bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
          const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
          saved = { id: `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`, startedAt: Date.now() };
          localStorage.setItem(key, JSON.stringify(saved));
        }
        payload.fieldSessionId = saved.id;
      }
      const res = await axios.post(`/api/site-control/targets/${deviceId}/action`, payload,
        { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.attachment && payload.fieldSessionId) {
        const key = `lgFieldAttachments:${payload.fieldSessionId}`;
        try {
          const previous = JSON.parse(sessionStorage.getItem(key) || '[]');
          sessionStorage.setItem(key, JSON.stringify([...previous.slice(-4), { targetDeviceId: deviceId,
            attemptId: payload.requestId, ...res.data.attachment }]));
        } catch { res.data.attachmentRetentionFailed = true; }
      }
      return res.data;
    },
    sendAndroidTvRemoteAction: async ({ deviceId, action, repeat, intervalMs, requestId, controlMethod }) => {
      const res = await axios.post(
        `/api/site-control/targets/${deviceId}/action`,
        { action, repeat, intervalMs, requestId, controlMethod, fieldSessionId: currentLgFieldSessionId(siteControlTarget?.siteId) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data;
    },
    openBluetoothSettings: async ({ deviceId }) => {
      const res = await axios.post(
        `/api/site-control/targets/${deviceId}/bluetooth-settings`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data;
    },
    sendSocketCommand: ({ deviceId, command, value, id }) => {
      if (!socket || !deviceId) return null;
      socket.emit('sendCommand', { deviceId, command, value, id });
      return id;
    },
    subscribe: ({ deviceId, onResponse, onMissing }) => {
      if (!socket || !deviceId) return () => {};

      const responseHandler = (payload) => {
        if (!payload || Number(payload.deviceId) !== Number(deviceId)) return;
        onResponse(payload);
      };
      const missingHandler = (payload) => {
        if (!payload || Number(payload.deviceId) !== Number(deviceId)) return;
        onMissing(payload);
      };

      socket.on('deviceCommandResponse', responseHandler);
      socket.on('deviceNotConnected', missingHandler);

      return () => {
        socket.off('deviceCommandResponse', responseHandler);
        socket.off('deviceNotConnected', missingHandler);
      };
    },
  }), [socket, token, siteControlTarget?.siteId]);

  const {
    appsStatus,
    canPowerOff,
    canPowerOn,
    canPreviewSnapshot,
    deviceId,
    deviceName,
    fetchLiveSnapshot,
    handleBackgroundIntervalChange,
    handlePrimaryRemoteCommand,
    cancelRemoteInput,
    handleRemoteInputEnd,
    handleInputDelayChange,
    handleLaunchSelectedApp,
    handleLaunchSuitestream,
    handleLgConnect,
    handleLgDisconnect,
    handleLgPairFromScan,
    handleLgScan,
    handleLgWake,
    handlePowerOff,
    handlePowerOn,
    handlePreviewPresetChange,
    handleRefreshApps,
    handleSaveLgSettings,
    handleSleepOnLan,
    handleWakeOnLan,
    hdmiSources,
    backgroundIntervalMs,
    inputDelayMs,
    isAppDevice,
    lastCommandAttempt,
    commandAttemptHistory,
    lgIpInput,
    lgLastAction,
    lgMacInput,
    lgRemoteReady,
    lgSaving,
    lgScanMessage,
    lgScanResults,
    lgScanStatus,
    lgStatus,
    lgStatusMessage,
    liveSnapshot,
    liveSnapshotLabel,
    loadApps,
    methodOptions,
    selectedMethodAvailable,
    normalizedLgMac,
    normalizedWolMac,
    orderedApps,
    persistWolMac,
    previewPreset,
    protectedVideoLikely,
    remoteAvailableCommands,
    selectedAppPackage,
    selectedMethod,
    setLgIpInput,
    setLgMacInput,
    setSelectedAppPackage,
    setSelectedMethod,
    setWolMacInput,
    showWolAction,
    snapshotMessage,
    snapshotStatus,
    snapshotUnsupportedMessage,
    tvHomeSource,
    wolMacInput,
    wolSaving,
  } = useSharedRemoteRuntime({
    allowAdbRemote: true,
    adbInputBlocked: rebootBusy,
    adbHdmiPorts,
    device: augmentedDevice,
    onDeviceUpdated,
    onSourceTargetSelected,
    transport,
    previewEnabled: previewOpen && previewProvider !== 'adb_remote',
    lgPreviewEnabled: previewProvider === 'lg_webos_remote',
    previewWindowVisible,
    nativePreviewForAdb: previewProvider === 'native',
  });

  useEffect(() => {
    setPreviewOpen(false);
    setPreviewProvider(selectedMethod === 'adb_remote' ? 'adb_remote'
      : selectedMethod === 'webos' ? 'lg_webos_remote' : 'native');
  }, [device?.id, selectedMethod]);

  useEffect(() => {
    if (selectedMethod === 'adb_remote' && device?.is_app) void hdmiInputs.refresh();
  }, [selectedMethod, device?.id, device?.is_app, hdmiInputs.refresh]);

  if (!deviceId) {
    return <div className="dashboard-app-launcher-empty">Select a device.</div>;
  }

  return (
    <div className="dashboard-tv-remote">
      {siteControlTarget?.sourceLayout?.requiresScreenContext && <label>Choose the Screen for this shared source<select defaultValue=""
        onChange={async (event) => {
          const selectedScreenId = event.target.value;
          if (!selectedScreenId) return;
          const generation = sourceContextScope.current.generation;
          setSourceContextError('');
          try {
            const result = await axios.get(`/api/site-control/targets/${deviceId}/status`, {
              params: { screenId: selectedScreenId }, headers: { Authorization: `Bearer ${token}` },
            });
            if (sourceContextScope.current.generation === generation) setSiteControlTarget(result.data.target);
          } catch (_) { if (sourceContextScope.current.generation === generation) setSourceContextError('Could not load that Screen. Choose a Screen again.'); }
        }}>
        <option value="">Select a Screen…</option>
        {siteControlTarget.sourceLayout.screens.map((screen) => <option key={screen.id} value={screen.id}>{screen.name}</option>)}
      </select></label>}
      {sourceContextError && <p role="status">{sourceContextError}</p>}
      {selectedMethod === 'webos' && augmentedDevice?.capabilities?.lgTarget && <section className="dashboard-tv-remote-panel">
        <h4>{deviceName} · LG commissioning</h4>
        <p>{siteControlTarget.host} · {siteControlTarget.paired ? 'Paired; reachability unverified until Connect or a control succeeds' : 'Not paired'}
          {' · '}Relay {siteControlTarget.lastBridgeDeviceId || siteControlTarget.controlledBy || 'automatic selection'}
          {' · '}Last seen: {siteControlTarget.lastConnectedAt || 'unknown'}</p>
        <button type="button" onClick={handleLgConnect} disabled={!siteControlTarget.paired}>Connect</button>
        <button type="button" onClick={() => handlePrimaryRemoteCommand('nav_down')} disabled={!siteControlTarget.paired}>Test (Down)</button>
        <button type="button" disabled={!siteControlTarget.paired} onClick={() => handlePrimaryRemoteCommand('lg_op_reconnect')}>Reconnect</button>
        <button type="button" disabled={!siteControlTarget.paired} onClick={async () => {
          const scope = inputScope.current;
          for (const query of ['inputs', 'foreground', 'power', 'volume']) {
            if (inputScope.current !== scope) return;
            await handlePrimaryRemoteCommand(`lg_op_${query}`, { onResult: (result) => {
              if (query === 'inputs') setLgInputs(readNativeInputReport(result.target)?.inputs || result.observation?.value?.devices || []);
            } });
          }
        }}>Refresh State</button>
        <button type="button" onClick={cancelRemoteInput}>Stop unsent input</button>
        <p>{lgInputs.length ? 'Reported input destinations' : 'HDMI candidates; sockets not confirmed until inventory is read'}</p>
        <div>{(lgInputs.length ? lgInputs : [1, 2, 3, 4].map((port) => ({ id: `HDMI_${port}`, label: `HDMI ${port} candidate` }))).map((input) =>
          <button type="button" key={input.id} disabled={!siteControlTarget.paired} onClick={() => handlePrimaryRemoteCommand('lg_op_input', { operationParams: { id: input.id } })}>{input.label || input.id}</button>)}</div>
        <p>Connection: {lgStatus === 'unpaired' ? 'not checked' : lgStatus}</p>
        <p role="status">{lgStatusMessage || siteControlTarget.lastError || 'Saved pairing is managed in Site Control. Open Site Control to pair or renew approval.'}</p>
      </section>}
      {selectedMethod === 'webos' && !augmentedDevice?.capabilities?.lgTarget && (
        <div className="dashboard-tv-remote-lg-grid">
          <section className="dashboard-tv-remote-panel">
            <h4>LG TV Connection</h4>
            <div className="dashboard-tv-remote-field">
              <span className="dashboard-tv-remote-label">LG TV IP Address</span>
              <input
                type="text"
                className="dashboard-tv-remote-input"
                placeholder="192.168.1.120"
                value={lgIpInput}
                onChange={(event) => setLgIpInput(event.target.value)}
              />
            </div>
            <div className="dashboard-tv-remote-field">
              <span className="dashboard-tv-remote-label">LG TV MAC Address</span>
              <input
                type="text"
                className="dashboard-tv-remote-input"
                placeholder="AA:BB:CC:DD:EE:FF"
                value={lgMacInput}
                onChange={(event) => setLgMacInput(event.target.value)}
              />
              <span className="dashboard-tv-remote-hint">
                {normalizedLgMac ? `Normalized: ${normalizedLgMac}` : 'Used for Wake-on-LAN and pairing context.'}
              </span>
            </div>
            <div className="dashboard-tv-remote-actions">
              <button type="button" className="secondary" onClick={handleSaveLgSettings} disabled={lgSaving}>
                Save LG Settings
              </button>
              <button type="button" className="secondary" onClick={handleLgConnect}>
                Connect / Pair
              </button>
              <button type="button" className="secondary" onClick={handleLgDisconnect}>
                Disconnect
              </button>
              <button type="button" className="primary" onClick={handleLgWake}>
                Power On (WOL)
              </button>
            </div>
            <div className="dashboard-tv-remote-lg-status-row">
              <span className={`dashboard-tv-remote-lg-badge status-${lgStatus}`}>{lgStatus || 'unknown'}</span>
              <span className="dashboard-tv-remote-hint">
                {lgStatusMessage || 'Use connect/pair to establish the LG TV session.'}
              </span>
            </div>
            {lgLastAction && (
              <div className="dashboard-tv-remote-hint">Last LG action: {lgLastAction}</div>
            )}
          </section>

          <section className="dashboard-tv-remote-panel">
            <div className="dashboard-tv-remote-panel-header">
              <h4>Scan and Pair</h4>
              <button
                type="button"
                className="secondary"
                onClick={handleLgScan}
                disabled={lgScanStatus === 'scanning'}
              >
                {lgScanStatus === 'scanning' ? 'Scanning…' : 'Scan Network'}
              </button>
            </div>
            <div className="dashboard-tv-remote-hint">
              {lgScanMessage || 'Run the local network scan from this device and pair any detected LG TV.'}
            </div>
            {lgScanResults.length > 0 ? (
              <div className="dashboard-tv-remote-scan-table-wrapper">
                <table className="dashboard-tv-remote-scan-table">
                  <thead>
                    <tr>
                      <th>IP</th>
                      <th>Name</th>
                      <th>MAC</th>
                      <th>Candidate</th>
                      <th>Ports</th>
                      <th>Capabilities</th>
                      <th>Pairing</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lgScanResults.map((deviceInfo) => {
                      const canPair =
                        deviceInfo.requiresPairing &&
                        !deviceInfo.paired &&
                        (deviceInfo.candidateType === 'lg_webos' || deviceInfo.type === 'lg_webos_tv');
                      return (
                        <tr key={deviceInfo.ip}>
                          <td>{deviceInfo.ip}</td>
                          <td>{deviceInfo.name || '—'}</td>
                          <td>{deviceInfo.mac || '—'}</td>
                          <td>{formatDiscovery(deviceInfo)}</td>
                          <td>{formatPorts(deviceInfo)}</td>
                          <td>{formatCapabilities(deviceInfo)}</td>
                          <td>{formatPairingStatus(deviceInfo)}</td>
                          <td>
                            {canPair ? (
                              <button type="button" className="secondary" onClick={() => handleLgPairFromScan(deviceInfo)}>
                                Pair
                              </button>
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="dashboard-tv-remote-empty">No LG scan results yet.</div>
            )}
          </section>
        </div>
      )}

      {selectedMethod === 'wol' && (
        <section className="dashboard-tv-remote-panel">
          <h4>Wake / Sleep on LAN</h4>
          <div className="dashboard-tv-remote-field">
            <span className="dashboard-tv-remote-label">Device MAC Address</span>
            <input
              type="text"
              className="dashboard-tv-remote-input"
              placeholder="F0:35:75:AA:BB:CC"
              value={wolMacInput}
              onChange={(event) => setWolMacInput(event.target.value)}
            />
            <span className="dashboard-tv-remote-hint">
              {normalizedWolMac
                ? `Normalized: ${normalizedWolMac}`
                : 'Paste any MAC format. The saved MAC is reused for wake and sleep packet testing.'}
            </span>
          </div>
          <div className="dashboard-tv-remote-actions">
            <button
              type="button"
              className="secondary"
              onClick={persistWolMac}
              disabled={!normalizedWolMac || wolSaving}
            >
              {wolSaving ? 'Saving…' : 'Save MAC'}
            </button>
            <button
              type="button"
              className="primary"
              onClick={handleWakeOnLan}
              disabled={!normalizedWolMac}
            >
              Send Wake Packet
            </button>
            <button
              type="button"
              className="secondary"
              onClick={handleSleepOnLan}
              disabled={!normalizedWolMac}
            >
              Send Sleep Packet
            </button>
          </div>
          <div className="dashboard-tv-remote-hint">
            This transport is packet-based only. Use the debug console below to confirm whether the native device channel accepted the request.
          </div>
        </section>
      )}

      {selectedMethod === 'assistant' && (canPowerOn || canPowerOff) && (
        <section className="dashboard-tv-remote-panel">
          <h4>Assistant Power</h4>
          <div className="dashboard-tv-remote-actions">
            {canPowerOn && (
              <button
                type="button"
                className="primary"
                onClick={handlePowerOn}
              >
                Power On
              </button>
            )}
            {canPowerOff && (
              <button
                type="button"
                className="secondary"
                onClick={handlePowerOff}
              >
                Power Off
              </button>
            )}
          </div>
          <div className="dashboard-tv-remote-hint">
            Google Assistant uses explicit power actions here. The single remote power key is hidden for this method.
          </div>
        </section>
      )}

      <header className="dashboard-tv-remote-toolbar">
        <div>
          <h3>TV Remote</h3>
          <p>{deviceName}</p>
        </div>
        <label className="dashboard-tv-remote-method">
          <span>Control with</span>
          <select className="dashboard-tv-remote-input" value={selectedMethod}
            onChange={(event) => setSelectedMethod(event.target.value)}>
            {methodOptions.map((option) => <option key={option.value} value={option.value} disabled={!option.enabled}>{option.label}</option>)}
          </select>
        </label>
      </header>
      {!selectedMethodAvailable && <p role="status" className="dashboard-tv-remote-hint">This connection is unavailable. Choose another control method.</p>}

      {selectedMethod !== 'wol' && <div className="dashboard-tv-remote-stage">
        <div className="dashboard-tv-remote-workspace">
          <section className="dashboard-tv-remote-preview">
            <label className="dashboard-tv-remote-field dashboard-tv-remote-preview-provider"><span className="dashboard-tv-remote-label">Preview with</span>
              <select className="dashboard-tv-remote-input" value={previewProvider}
                onChange={(event) => { setPreviewOpen(false); setPreviewProvider(event.target.value); }}>
                <option value="native">Suitestream App</option>
                {selectedMethod === 'webos' && <option value="lg_webos_remote">LG TV</option>}
                <option value="adb_remote" disabled={!siteControlTarget?.adb}>ADB Remote</option>
              </select>
            </label>
            {previewProvider === 'adb_remote' ? <AdbRemotePreview key={`${deviceId}:adb_remote`} deviceId={deviceId}
              name={deviceName} disabled={rebootBusy || !siteControlTarget?.adb} /> : <>
            <div className="dashboard-tv-remote-panel-header">
              <h4>Live Preview</h4>
              <div className="dashboard-tv-remote-actions">
                <button type="button" className="secondary" onClick={() => setPreviewOpen((previous) => !previous)}>
                  {previewOpen ? 'Hide Preview' : 'Open Preview'}
                </button>
                <button type="button" className="secondary" onClick={() => fetchLiveSnapshot('manual')}
                  disabled={!canPreviewSnapshot || ['loading', 'refreshing'].includes(snapshotStatus) || Boolean(snapshotUnsupportedMessage)}>
                  Refresh Now
                </button>
                <PreviewPopout key={deviceId} name={deviceName}
                  image={previewOpen && canPreviewSnapshot ? liveSnapshot?.screenshot?.dataUrl : null}
                  caption={protectedVideoLikely ? 'Protected video may appear black.' : snapshotMessage || liveSnapshotLabel}
                  onOpen={() => setPreviewOpen(true)} onVisibilityChange={setPreviewWindowVisible}
                  onRefresh={() => fetchLiveSnapshot('manual')}
                  refreshDisabled={!canPreviewSnapshot || ['loading', 'refreshing'].includes(snapshotStatus) || Boolean(snapshotUnsupportedMessage)} />
              </div>
            </div>
            <div className="dashboard-tv-remote-preview-frame">
              {previewOpen && canPreviewSnapshot && liveSnapshot?.screenshot?.dataUrl ? (
                <img src={liveSnapshot.screenshot.dataUrl} alt={`Live preview for ${deviceName}`} className="dashboard-tv-remote-preview-image" />
              ) : <div className="dashboard-tv-remote-preview-empty">
                <svg width="48" height="38" viewBox="0 0 48 38" fill="none" aria-hidden="true">
                  <rect x="3" y="3" width="42" height="27" rx="4" stroke="currentColor" strokeWidth="2" />
                  <path d="M17 35h14M24 30v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <strong>{!previewOpen ? 'Your TV preview' : !canPreviewSnapshot ? 'Preview unavailable for this connection' : 'Waiting for a picture…'}</strong>
                <span>{!previewOpen ? 'Open Preview to see the screen here.' : snapshotUnsupportedMessage || (snapshotStatus === 'error' ? 'Could not capture the screen. Try Refresh Now.' : '')}</span>
              </div>}
            </div>
            <div className="dashboard-tv-remote-preview-caption" role="status">
              {protectedVideoLikely && previewOpen ? 'Protected video may appear black.'
                : previewOpen && canPreviewSnapshot ? (snapshotStatus === 'error' ? snapshotMessage : liveSnapshotLabel) : '\u00a0'}
            </div>
            {(canPowerOn || canPowerOff || showWolAction) && <div className="dashboard-tv-remote-actions">
              {canPowerOn && <button type="button" className="primary" onClick={handlePowerOn}>Power On</button>}
              {canPowerOff && <button type="button" className="secondary" onClick={handlePowerOff}>Power Off</button>}
              {showWolAction && <button type="button" className="secondary" onClick={handleWakeOnLan}>Wake TV</button>}
            </div>}
            <details className="dashboard-tv-remote-preview-options">
              <summary>Preview options</summary>
              <div className="dashboard-tv-remote-settings-inline">
                <label className="dashboard-tv-remote-field"><span className="dashboard-tv-remote-label">Refresh speed</span>
                  <select className="dashboard-tv-remote-input" value={previewPreset} disabled={!canPreviewSnapshot}
                    onChange={(event) => handlePreviewPresetChange(event.target.value)}>
                    {Object.entries(PREVIEW_PRESETS).map(([value, preset]) => <option key={value} value={value}>{preset.label}</option>)}
                    <option value="custom">Custom</option>
                  </select>
                </label>
                <label className="dashboard-tv-remote-field"><span className="dashboard-tv-remote-label">After input (seconds)</span>
                  <input className="dashboard-tv-remote-input" type="number" min="0" step="0.5" disabled={!canPreviewSnapshot}
                    value={inputDelayMs / 1000} onChange={(event) => handleInputDelayChange(Math.round(Number(event.target.value || 0) * 1000))} />
                </label>
                <label className="dashboard-tv-remote-field"><span className="dashboard-tv-remote-label">Auto refresh (seconds)</span>
                  <input className="dashboard-tv-remote-input" type="number" min="0" step="1" disabled={!canPreviewSnapshot}
                    value={backgroundIntervalMs / 1000} onChange={(event) => handleBackgroundIntervalChange(Math.round(Number(event.target.value || 0) * 1000))} />
                </label>
              </div>
            </details>
            </>}
          </section>

          <section className="dashboard-tv-remote-panel dashboard-tv-remote-apps">
            <div className="dashboard-tv-remote-panel-header"><h4>App launcher</h4>
              {isAppDevice && <button type="button" className="secondary" onClick={() => setAppPickerDeviceId(deviceId)}>Manage apps</button>}
            </div>
            {selectedMethod === 'webos' && lgRemoteReady ? <RemoteAppPicker key={`native-${deviceId}`} deviceId={deviceId} name={deviceName} nativeConnector="lg_webos_remote" /> : isAppDevice ? <>
              <label className="dashboard-tv-remote-field"><span className="dashboard-tv-remote-label">Your apps</span>
                <select className="dashboard-tv-remote-input" value={selectedAppPackage}
                  onChange={(event) => setSelectedAppPackage(event.target.value)} disabled={!orderedApps.length}>
                  {!orderedApps.length && <option value="">No apps selected</option>}
                  {orderedApps.map((app) => <option key={app.package_name} value={app.package_name}>{app.app_name || app.package_name}</option>)}
                </select>
              </label>
              <div className="dashboard-tv-remote-actions">
                <button type="button" className="primary" onClick={handleLaunchSelectedApp} disabled={!selectedAppPackage}>Launch App</button>
                <button type="button" className="secondary" onClick={handleLaunchSuitestream}>Launch Suitestream</button>
              </div>
              {appsStatus === 'loading' && <p className="dashboard-tv-remote-hint">Loading apps…</p>}
              {appsStatus === 'error' && <p role="status">Could not load apps. Open Manage apps to refresh the list.</p>}
              {appsStatus === 'ready' && !orderedApps.length && <p className="dashboard-tv-remote-hint">Choose apps with Manage apps to add them here.</p>}
              {appPickerDeviceId === deviceId && <RemoteAppPicker key={deviceId} deviceId={deviceId} name={deviceName}
                onRequestRefresh={handleRefreshApps} onClose={() => setAppPickerDeviceId(null)}
                onSaved={async () => { await loadApps(); setAppPickerDeviceId(null); }} />}
            </> : <p className="dashboard-tv-remote-hint">App launching is unavailable for this device.</p>}
          </section>
        </div>

        <section className="dashboard-tv-remote-control-shell">
          <div className="dashboard-tv-remote-panel-header"><h4>Remote</h4><span className="dashboard-tv-remote-control-chip">{METHOD_LABELS[selectedMethod] || selectedMethod}</span></div>
          <div className={`dashboard-tv-remote-remote ${selectedMethod === 'webos' && !lgRemoteReady ? 'is-disabled' : ''}`}>
            <UnifiedTvControl key={`${deviceId}:${selectedMethod}`} commands={selectedMethod === 'webos' ? LG_TV_COMMANDS.map((command) => command.command === 'power'
              ? { ...command, label: 'Power Off', icon: '⏻ Off' } : command) : UNIFIED_TV_COMMANDS}
              onCommand={handlePrimaryRemoteCommand} onInputEnd={handleRemoteInputEnd} repeatLimit={selectedMethod === 'webos' ? 20 : null}
              available={rebootBusy && selectedMethod === 'adb_remote' ? {} : remoteAvailableCommands}
              moreControls={<div className="dashboard-tv-remote-more-actions">
                {sourceContextError && <p role="status">{sourceContextError}</p>}
      {selectedMethod === 'webos' && augmentedDevice?.capabilities?.lgTarget && <LgCommissioningPanel
                  deviceId={deviceId} name={deviceName} siteId={siteControlTarget?.siteId} onInputs={setLgInputs} onCommand={handlePrimaryRemoteCommand} onStop={cancelRemoteInput} />}
                {['adb_remote', 'android_tv_remote', 'webos'].includes(selectedMethod) && <RemoteFieldResults
                  key={`${deviceId}:${selectedMethod}`} deviceId={deviceId} method={selectedMethod === 'webos' ? 'lg_webos_remote' : selectedMethod}
                  attempts={commandAttemptHistory} />}
                {selectedMethod === 'adb_remote' && siteControlTarget?.siteId && <AdbRemoteReboot
                  siteId={siteControlTarget.siteId} deviceId={deviceId} name={deviceName}
                  onBusyChange={setRebootBusy} onBeforeRestart={cancelRemoteInput} />}
                {augmentedDevice?.is_app && <button type="button" className="secondary" disabled={rebootBusy}
                  onClick={() => handlePrimaryRemoteCommand('bluetooth_settings')}>Bluetooth Settings</button>}
              </div>}
              forceSourceAvailability={false} adbPowerCandidates={selectedMethod === 'adb_remote'}
              hdmiSources={selectedMethod === 'adb_remote' ? hdmiInputs.sources : hdmiSources}
              sourceCandidates={selectedMethod === 'adb_remote'} tvHomeSource={tvHomeSource} />
          </div>
          {['failed', 'timed_out'].includes(lastCommandAttempt?.status) && <p role="alert" className="dashboard-tv-remote-error">{commandAttemptSummary(lastCommandAttempt)}</p>}
        </section>
      </div>}
    </div>
  );
}

export default TvRemotePanel;
